import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';
import { decodeFileName } from '../utils/fileNameUtils';

const DEFAULT_FOLDER_INFO_ERROR = 'The folder information could not be calculated.';
export const MAX_FOLDER_INFO_SELECTION = 50;

export const folderInfoApiOrigin = () => {
    const configured = String(process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');
    if (configured) return configured;
    if (typeof window === 'undefined') return '';

    const { protocol, hostname, origin } = window.location;
    if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('api.')) {
        return origin;
    }

    const labels = hostname.split('.').filter(Boolean);
    const applicationRoot = labels.length > 2 ? labels.slice(-2).join('.') : hostname;
    return `${protocol}//api.${applicationRoot}`;
};

export const getFolderInfoEndpoint = () => `${folderInfoApiOrigin()}/folder-info.php`;

const stringValue = (value) => typeof value === 'string' ? value.trim() : '';
const storageKeyValue = (value) => typeof value === 'string' ? value : '';

const safeDecodeURIComponent = (value) => {
    try {
        return decodeURIComponent(value);
    } catch (error) {
        return value;
    }
};

const displayFolderName = (value) => decodeFileName(safeDecodeURIComponent(stringValue(value)));

const canonicalFolderKey = (value) => {
    // S3 keys are opaque. Keep percent sequences literal while comparing
    // selections so folders such as "%2F" and "/" are never collapsed.
    const raw = storageKeyValue(value);
    return raw.endsWith('/') ? raw.slice(0, -1) : raw;
};

const itemKey = (item) => storageKeyValue(item?.key || item?.folder_key || item?.file?.key || item?.file_path);

const isFolderItem = (item) => item?.type === 'folder' || item?.isFolder === true || item?.is_folder === true;

const isFileItem = (item) => item?.type === 'file' || Boolean(item?.file) || item?.isFolder === false || item?.is_folder === false;

const folderNameFromKey = (key) => {
    const normalized = canonicalFolderKey(key);
    const segment = normalized.split('/').filter(Boolean).pop() || 'Folder';
    return displayFolderName(segment) || 'Folder';
};

/**
 * Resolves the currently selected keys to folder targets while preserving the
 * raw encoded S3 key. Known folders receive only the required trailing slash.
 * Unknown off-page selections are accepted only when that slash is present.
 */
export const buildSelectedFolderInfoTargets = (selectedKeys, filteredItems) => {
    const itemsByCanonicalKey = new Map();

    (Array.isArray(filteredItems) ? filteredItems : []).forEach((item) => {
        const key = itemKey(item);
        const canonical = canonicalFolderKey(key);
        if (!key || !canonical) return;

        const existing = itemsByCanonicalKey.get(canonical);
        if (!existing || isFolderItem(item)) {
            itemsByCanonicalKey.set(canonical, item);
        }
    });

    const seen = new Set();
    const targets = [];

    (Array.isArray(selectedKeys) ? selectedKeys : []).forEach((selectedKey) => {
        const rawKey = storageKeyValue(selectedKey);
        const canonical = canonicalFolderKey(rawKey);
        if (!rawKey || !canonical || seen.has(canonical)) return;

        const matchedItem = itemsByCanonicalKey.get(canonical);
        if (matchedItem && isFileItem(matchedItem) && !isFolderItem(matchedItem)) return;

        const isKnownFolder = Boolean(matchedItem && isFolderItem(matchedItem));
        const isOffPageFolder = !matchedItem && rawKey.endsWith('/');
        if (!isKnownFolder && !isOffPageFolder) return;

        seen.add(canonical);
        const matchedName = displayFolderName(matchedItem?.name || matchedItem?.folder_name || '');
        targets.push({
            key: rawKey.endsWith('/') ? rawKey : `${rawKey}/`,
            name: matchedName || folderNameFromKey(rawKey),
        });
    });

    return targets;
};

const nonNegativeNumber = (value, fallback = 0) => {
    if (value === null || value === undefined || value === '') return fallback;
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? number : fallback;
};

const firstDefined = (source, keys) => {
    if (!source || typeof source !== 'object') return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
            return source[key];
        }
    }
    return undefined;
};

const normalizeFolderRow = (row, fallbackKey = '') => {
    const source = row && typeof row === 'object' ? row : {};
    const key = storageKeyValue(firstDefined(source, ['key', 'folder_key', 'prefix', 'path']) || fallbackKey);
    const fileCount = nonNegativeNumber(firstDefined(source, ['file_count', 'files_count', 'total_files', 'files']));
    const folderCount = nonNegativeNumber(firstDefined(source, ['folder_count', 'folders_count', 'total_folders', 'subfolder_count']));
    const childValue = firstDefined(source, ['child_count', 'children_count', 'total_children', 'item_count', 'total_items']);

    return {
        key,
        name: displayFolderName(firstDefined(source, ['name', 'folder_name', 'label'])) || folderNameFromKey(key),
        total_bytes: nonNegativeNumber(firstDefined(source, ['total_bytes', 'size_bytes', 'bytes', 'total_size', 'size'])),
        file_count: fileCount,
        folder_count: folderCount,
        child_count: nonNegativeNumber(childValue, fileCount + folderCount),
        error: stringValue(firstDefined(source, ['error', 'warning'])),
    };
};

const normalizeFolderRows = (value) => {
    if (Array.isArray(value)) {
        return value.map((row) => normalizeFolderRow(row));
    }

    if (value && typeof value === 'object') {
        return Object.entries(value).map(([key, row]) => normalizeFolderRow(row, key));
    }

    return [];
};

const stringList = (value) => {
    const entries = Array.isArray(value) ? value : value ? [value] : [];
    return entries
        .map((entry) => {
            if (typeof entry === 'string') return entry.trim();
            if (entry && typeof entry === 'object') {
                return stringValue(entry.message || entry.error || entry.warning);
            }
            return '';
        })
        .filter(Boolean);
};

export const normalizeFolderInfoResponse = (payload) => {
    const source = payload && typeof payload === 'object' ? payload : {};
    const nested = source.data && typeof source.data === 'object' ? source.data : {};
    const folders = normalizeFolderRows(source.folders ?? source.results ?? nested.folders);
    const summed = folders.reduce((totals, folder) => ({
        total_bytes: totals.total_bytes + folder.total_bytes,
        file_count: totals.file_count + folder.file_count,
        folder_count: totals.folder_count + folder.folder_count,
        child_count: totals.child_count + folder.child_count,
    }), { total_bytes: 0, file_count: 0, folder_count: 0, child_count: 0 });
    const rawTotals = source.totals || source.total || source.summary || nested.totals || source;
    const totalFileCount = nonNegativeNumber(
        firstDefined(rawTotals, ['file_count', 'files_count', 'total_files']),
        summed.file_count
    );
    const totalFolderCount = nonNegativeNumber(
        firstDefined(rawTotals, ['folder_count', 'folders_count', 'total_folders']),
        summed.folder_count
    );
    const totalChildValue = firstDefined(rawTotals, ['child_count', 'children_count', 'total_children', 'item_count', 'total_items']);
    const warnings = [
        ...stringList(source.warnings),
        ...stringList(source.errors),
        ...folders.map((folder) => folder.error).filter(Boolean),
    ].filter((warning, index, all) => all.indexOf(warning) === index);

    return {
        success: source.success !== false,
        partial_success: Boolean(source.partial_success || source.partial || warnings.length > 0),
        folders,
        totals: {
            total_bytes: nonNegativeNumber(
                firstDefined(rawTotals, ['total_bytes', 'size_bytes', 'bytes', 'total_size', 'size']),
                summed.total_bytes
            ),
            file_count: totalFileCount,
            folder_count: totalFolderCount,
            child_count: nonNegativeNumber(totalChildValue, totalFileCount + totalFolderCount),
        },
        warnings,
    };
};

export const formatFolderInfoBytes = (value) => {
    const bytes = nonNegativeNumber(value);
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const amount = bytes / (1024 ** unitIndex);
    const maximumFractionDigits = unitIndex === 0 ? 0 : 2;

    return `${amount.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits,
    })} ${units[unitIndex]}`;
};

const normalizeRequestFolderKeys = (folderKeys) => {
    const seen = new Set();
    const normalized = [];

    (Array.isArray(folderKeys) ? folderKeys : []).forEach((entry) => {
        const rawKey = storageKeyValue(typeof entry === 'string' ? entry : entry?.key || entry?.folder_key);
        const canonical = canonicalFolderKey(rawKey);
        if (!rawKey || !canonical || seen.has(canonical)) return;
        seen.add(canonical);
        normalized.push(rawKey.endsWith('/') ? rawKey : `${rawKey}/`);
    });

    return normalized;
};

const responseMessage = (payload, fallback) => {
    const message = payload && typeof payload === 'object'
        ? payload.message || payload.error || payload.detail
        : '';
    return stringValue(message) || fallback;
};

const readJsonResponse = async (response) => {
    const text = await response.text();
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch (error) {
        return null;
    }
};

/**
 * Requests recursive folder statistics from the authenticated CaseDB API.
 */
export const requestFolderInfo = async ({
    caseId,
    folderKeys,
    signal,
    fetchImpl,
    endpoint = getFolderInfoEndpoint(),
} = {}) => {
    const normalizedCaseId = Number(caseId);
    if (!Number.isInteger(normalizedCaseId) || normalizedCaseId <= 0) {
        throw new Error('A valid case is required to calculate folder information.');
    }

    const normalizedFolderKeys = normalizeRequestFolderKeys(folderKeys);
    if (normalizedFolderKeys.length === 0) {
        throw new Error('Select at least one folder to calculate its information.');
    }
    if (normalizedFolderKeys.length > MAX_FOLDER_INFO_SELECTION) {
        throw new Error(`Select up to ${MAX_FOLDER_INFO_SELECTION} folders at a time.`);
    }

    const requestFetch = typeof fetchImpl === 'function'
        ? fetchImpl
        : (typeof window !== 'undefined' && typeof window.fetch === 'function' ? window.fetch.bind(window) : null);
    if (!requestFetch) {
        throw new Error(DEFAULT_FOLDER_INFO_ERROR);
    }

    await ensureCaseDbCsrfToken();
    const response = await requestFetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
        body: JSON.stringify({
            case_id: normalizedCaseId,
            folder_keys: normalizedFolderKeys,
        }),
        signal,
    });

    const payload = await readJsonResponse(response);
    if (!response.ok || payload?.success === false) {
        const statusSuffix = !response.ok && response.status ? ` (${response.status})` : '';
        throw new Error(responseMessage(payload, `${DEFAULT_FOLDER_INFO_ERROR}${statusSuffix}`));
    }
    if (!payload) {
        throw new Error('CaseDB returned an invalid response while calculating folder information.');
    }

    return normalizeFolderInfoResponse(payload);
};
